import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  CircleDollarSign,
  RefreshCw,
  Users,
} from "lucide-react";
import { TopBar } from "../components/TopBar";
import { DetailDialog } from "../components/DetailDialog";
import { LedgerColumn } from "../components/LedgerColumn";
import { ChangedRecently, NeedsAttention } from "../components/HomeOperations";
import { api, formatMoney } from "../lib/api";

const SummaryCell = ({ label, value, sub, icon: Icon, tone = "ink", testid }) => {
  const colors = {
    ink: "text-ledger-ink",
    danger: "text-[#B91C1C]",
    money: "text-[#78350F]",
    success: "text-[#047857]",
  };
  return (
    <div className="bg-white border border-ledger-border px-5 py-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.18em] text-ledger-muted">
        <span>{label}</span>
        <Icon className={`h-4 w-4 ${colors[tone]}`} aria-hidden="true" />
      </div>
      <div className={`mt-2 font-display text-3xl ${colors[tone]}`}>{value}</div>
      {sub && <div className="mt-1 font-mono text-[11px] text-ledger-muted">{sub}</div>}
    </div>
  );
};

const ClientCard = ({ client, onOpen }) => {
  const attention = client.counts.needs_attention;
  return (
    <button
      type="button"
      onClick={() => onOpen(client.slug)}
      data-testid={`client-card-${client.slug}`}
      className="group w-full text-left bg-white border border-ledger-border px-6 py-6 hover:border-ledger-ink hover:-translate-y-0.5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ledger-ink"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <div className="h-11 w-11 rounded-sm bg-ledger-ink text-white grid place-items-center shrink-0">
            <Building2 className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-2xl text-ledger-ink">{client.name}</div>
            <div className="mt-1 text-sm text-ledger-sub truncate">
              {client.primary_contacts.length ? client.primary_contacts.join(", ") : "Contact not identified"}
            </div>
          </div>
        </div>
        <ArrowUpRight className="h-4 w-4 text-ledger-muted group-hover:text-ledger-ink group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" aria-hidden="true" />
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4 border-t border-ledger-border pt-4">
        <div>
          <div className={`font-mono text-lg ${attention ? "text-[#B91C1C]" : "text-ledger-ink"}`}>
            {attention}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-ledger-muted">Need attention</div>
        </div>
        <div>
          <div className="font-mono text-lg text-ledger-ink">{client.counts.active}</div>
          <div className="text-[10px] uppercase tracking-wider text-ledger-muted">Active</div>
        </div>
        <div>
          <div className="font-mono text-lg text-[#78350F]">
            {formatMoney(client.money.outstanding_to_us || 0)}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-ledger-muted">Outstanding</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] text-ledger-muted">
        <span>{client.counts.promised_to_us} promised to us</span>
        <span>{client.counts.promised_by_us} promised by us</span>
        {client.counts.overdue > 0 && <span className="text-[#B91C1C]">{client.counts.overdue} overdue</span>}
      </div>
    </button>
  );
};

export default function Clients({ setPasteOpen }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [listData, setListData] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      if (slug) {
        setClient(await api.getClient(slug));
      } else {
        setListData(await api.listClients());
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const openCommitment = (commitment) => {
    setDetailId(commitment.id);
    setDetailOpen(true);
  };

  const allCommitments = client
    ? [...client.commitments.promised_to_us, ...client.commitments.promised_by_us]
    : [];
  const attentionData = client ? {
    items: client.needs_attention,
    count: client.needs_attention.length,
    total_requiring_attention: client.needs_attention.length,
  } : null;

  return (
    <div className="min-h-screen paper-grain" data-testid="clients-page">
      <TopBar onOpenPaste={() => setPasteOpen(true)} />
      <main className="mx-auto max-w-[1700px] px-6 md:px-12 py-10 md:py-14">
        {error ? (
          <div className="max-w-2xl mx-auto border border-[#EF4444] bg-[#FEF2F2] p-8 text-center" data-testid="clients-error">
            <AlertTriangle className="h-6 w-6 text-[#B91C1C] mx-auto" />
            <h1 className="mt-3 font-display text-3xl text-ledger-ink">Couldn&rsquo;t load this client view.</h1>
            <button onClick={load} className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ledger-ink text-white text-sm">
              <RefreshCw className="h-4 w-4" /> Try again
            </button>
          </div>
        ) : slug ? (
          <>
            <Link to="/app/clients" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-ledger-muted hover:text-ledger-ink">
              <ArrowLeft className="h-3.5 w-3.5" /> All clients
            </Link>

            {loading || !client ? (
              <div className="mt-8 h-64 border border-ledger-border bg-white animate-pulse" data-testid="client-detail-loading" />
            ) : (
              <>
                <header className="mt-6 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between animate-fadeUp">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted">Client relationship · Current truth</div>
                    <h1 className="mt-2 font-display text-5xl md:text-6xl text-ledger-ink" data-testid="client-name">{client.name}</h1>
                    <div className="mt-3 flex items-center gap-2 text-sm text-ledger-sub">
                      <Users className="h-4 w-4" />
                      {client.primary_contacts.length ? client.primary_contacts.join(", ") : "Contact not identified"}
                    </div>
                  </div>
                  <p className="max-w-xl text-sm text-ledger-sub lg:text-right">
                    What this client owes Northstar Creative, what Northstar owes the client, and what changed afterward.
                  </p>
                </header>

                <section className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="client-summary">
                  <SummaryCell label="Needs attention" value={client.counts.needs_attention} sub={`${client.counts.overdue} overdue`} icon={AlertTriangle} tone="danger" testid="client-attention-count" />
                  <SummaryCell label="Active commitments" value={client.counts.active} sub={`${client.counts.total} total tracked`} icon={Building2} testid="client-active-count" />
                  <SummaryCell label="Outstanding to us" value={formatMoney(client.money.outstanding_to_us || 0)} sub="current balance" icon={CircleDollarSign} tone="money" testid="client-outstanding" />
                  <SummaryCell label="Fulfilled" value={client.counts.fulfilled} sub="with retained history" icon={CheckCircle2} tone="success" testid="client-fulfilled-count" />
                </section>

                <div className="mt-12">
                  <NeedsAttention data={attentionData} loading={false} onOpen={openCommitment} />
                </div>

                <ChangedRecently events={client.recent_changes} loading={false} onOpen={openCommitment} />

                <section className="mt-14 border-t border-ledger-border pt-10" data-testid="client-current-commitments">
                  <div className="mb-8">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted">Current truth</div>
                    <h2 className="mt-1 font-display text-3xl text-ledger-ink">Commitments across this relationship</h2>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                    <LedgerColumn direction="to_me" title="Promised to us" subtitle="What the client owes Northstar" commitments={allCommitments} onOpen={openCommitment} />
                    <LedgerColumn direction="by_me" title="Promised by us" subtitle="What Northstar owes the client" commitments={allCommitments} onOpen={openCommitment} />
                  </div>
                </section>
              </>
            )}
          </>
        ) : (
          <>
            <header className="max-w-4xl animate-fadeUp">
              <div className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted">Northstar Creative · External relationships</div>
              <h1 className="mt-2 font-display text-5xl md:text-6xl text-ledger-ink">Client relationships</h1>
              <p className="mt-3 text-base text-ledger-sub max-w-3xl">
                A current view of what clients promised Northstar, what Northstar promised back, and where attention is required.
              </p>
            </header>

            {loading || !listData ? (
              <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="clients-loading">
                {[0, 1, 2].map((key) => <div key={key} className="h-64 border border-ledger-border bg-white animate-pulse" />)}
              </div>
            ) : (
              <>
                <div className="mt-8 font-mono text-[11px] uppercase tracking-wider text-ledger-muted">
                  {listData.count} active client relationships
                </div>
                <section className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="client-grid">
                  {listData.clients.map((item) => (
                    <ClientCard key={item.slug} client={item} onOpen={(clientSlug) => navigate(`/app/clients/${clientSlug}`)} />
                  ))}
                </section>
                {listData.unassigned_commitment_count > 0 && (
                  <div className="mt-6 border border-[#F59E0B] bg-[#FFFBEB] px-5 py-4 text-sm text-[#92400E]">
                    {listData.unassigned_commitment_count} commitment{listData.unassigned_commitment_count === 1 ? "" : "s"} need a client relationship confirmed.
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      <DetailDialog
        commitmentId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onChanged={load}
      />
    </div>
  );
}
